import {
  Badge,
  Box,
  Group,
  Paper,
  Progress,
  Stack,
  Text,
  Timeline,
  Title,
  ActionIcon,
  Tooltip,
  Loader,
  Center,
} from "@mantine/core";
import { IconCheck, IconClock, IconCircleDot, IconCircleCheck } from "@tabler/icons-react";
import { useMemo } from "react";
import { useMilestones, useUpdateMilestone, type Milestone } from "@/hooks/useMilestones";

interface ProjectTimelineProps {
  projectId: string;
  projectStartDate?: string | Date | null;
  projectEndDate?: string | Date | null;
}

// Standard construction phases with relative timing (% of project duration)
// Used as fallback when no milestones exist
const STANDARD_PHASES = [
  { name: "Mobilization", percent: 0 },
  { name: "Foundation", percent: 15 },
  { name: "Framing", percent: 35 },
  { name: "Rough-In", percent: 55 },
  { name: "Drywall", percent: 70 },
  { name: "Finishes", percent: 90 },
  { name: "Closeout", percent: 100 },
];

interface DisplayMilestone {
  id?: string;
  name: string;
  date: string;
  status: "completed" | "in_progress" | "planned" | "skipped";
  canComplete?: boolean;
}

/**
 * Generate fallback milestones based on project start/end dates
 * Used when no milestones exist in the database
 */
function generateFallbackMilestones(startDate: Date, endDate: Date): DisplayMilestone[] {
  const now = new Date();
  const totalDuration = endDate.getTime() - startDate.getTime();

  return STANDARD_PHASES.map((phase, index) => {
    const milestoneDate = new Date(startDate.getTime() + (totalDuration * phase.percent) / 100);

    // Determine status based on current date
    let status: DisplayMilestone["status"];
    const nextPhase = STANDARD_PHASES[index + 1];
    const nextDate = nextPhase
      ? new Date(startDate.getTime() + (totalDuration * nextPhase.percent) / 100)
      : endDate;

    if (now >= nextDate) {
      status = "completed";
    } else if (now >= milestoneDate) {
      status = "in_progress";
    } else {
      status = "planned";
    }

    return {
      name: phase.name,
      date: milestoneDate.toISOString().split("T")[0] || milestoneDate.toISOString(),
      status,
    };
  });
}

/**
 * Convert API milestones to display format
 */
function convertToDisplayMilestones(milestones: Milestone[]): DisplayMilestone[] {
  return milestones.map((m) => ({
    id: m.id,
    name: m.name,
    date: m.targetDate || new Date().toISOString(),
    status: m.effectiveStatus,
    canComplete: m.effectiveStatus !== "completed" && m.effectiveStatus !== "skipped",
  }));
}

/**
 * ProjectTimeline - Visual timeline showing project progress with milestones
 *
 * Fetches milestones from the database and displays them.
 * Falls back to auto-generated milestones if none exist.
 *
 * Displays:
 * - Key milestones with completion status
 * - Current date marker
 * - Overall progress percentage
 * - Schedule status (on track / behind / ahead)
 * - Ability to mark milestones as complete
 */
export function ProjectTimeline({
  projectId,
  projectStartDate,
  projectEndDate,
}: ProjectTimelineProps) {
  // Fetch milestones from API
  const { data: milestonesData, isLoading } = useMilestones(projectId);
  const updateMilestone = useUpdateMilestone(projectId);

  // Convert API milestones to display format or generate fallback
  const displayMilestones = useMemo(() => {
    if (milestonesData?.milestones && milestonesData.milestones.length > 0) {
      return convertToDisplayMilestones(milestonesData.milestones);
    }

    // Fallback: generate milestones from project dates
    const start = projectStartDate ? new Date(projectStartDate) : new Date();
    const end = projectEndDate && projectEndDate !== null
      ? new Date(projectEndDate)
      : new Date(start.getTime() + 180 * 24 * 60 * 60 * 1000); // 6 months

    return generateFallbackMilestones(start, end);
  }, [milestonesData?.milestones, projectStartDate, projectEndDate]);

  // Calculate progress and schedule status
  const { progressPercentage, scheduleStatus, daysBehind, daysAhead } = useMemo(() => {
    const now = new Date();
    const startDate = projectStartDate
      ? new Date(projectStartDate)
      : new Date(displayMilestones[0]?.date || now);
    const endDate = projectEndDate && projectEndDate !== null
      ? new Date(projectEndDate)
      : new Date(displayMilestones[displayMilestones.length - 1]?.date || now);

    const totalDays = Math.max(
      1,
      Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24))
    );
    const elapsedDays = Math.ceil((now.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
    const timeProgress = Math.min(100, Math.max(0, (elapsedDays / totalDays) * 100));

    const completedCount = displayMilestones.filter((m) => m.status === "completed").length;
    const totalMilestones = displayMilestones.length;
    const milestoneProgress = totalMilestones > 0 ? (completedCount / totalMilestones) * 100 : 0;

    // Determine schedule status
    const progressDiff = milestoneProgress - timeProgress;
    let status: "on_track" | "behind" | "ahead";
    let behind = 0;
    let ahead = 0;

    if (progressDiff < -10) {
      status = "behind";
      behind = Math.round((Math.abs(progressDiff) / 100) * totalDays);
    } else if (progressDiff > 10) {
      status = "ahead";
      ahead = Math.round((progressDiff / 100) * totalDays);
    } else {
      status = "on_track";
    }

    return {
      progressPercentage: Math.round(milestoneProgress),
      scheduleStatus: status,
      daysBehind: behind,
      daysAhead: ahead,
    };
  }, [projectStartDate, projectEndDate, displayMilestones]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const getStatusColor = () => {
    if (scheduleStatus === "ahead") return "green";
    if (scheduleStatus === "behind") return "red";
    return "blue";
  };

  const getStatusText = () => {
    if (scheduleStatus === "ahead") return `${daysAhead} days ahead`;
    if (scheduleStatus === "behind") return `${daysBehind} days behind`;
    return "On track";
  };

  const getStatusIcon = (status: DisplayMilestone["status"]) => {
    switch (status) {
      case "completed":
        return <IconCheck size={18} />;
      case "in_progress":
        return <IconClock size={18} />;
      case "skipped":
        return <IconCircleDot size={18} style={{ opacity: 0.5 }} />;
      case "planned":
      default:
        return <IconCircleDot size={18} />;
    }
  };

  const getStatusColor2 = (status: DisplayMilestone["status"]) => {
    switch (status) {
      case "completed":
        return "green";
      case "in_progress":
        return "blue";
      case "skipped":
        return "gray";
      case "planned":
      default:
        return "gray";
    }
  };

  const handleMarkComplete = async (milestone: DisplayMilestone) => {
    if (!milestone.id) return;

    await updateMilestone.mutateAsync({
      milestoneId: milestone.id,
      data: {
        status: "completed",
        completedAt: new Date().toISOString(),
      },
    });
  };

  if (isLoading) {
    return (
      <Paper p="xl" withBorder>
        <Center py="xl">
          <Loader size="md" />
        </Center>
      </Paper>
    );
  }

  return (
    <Paper p="xl" withBorder>
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between" align="flex-start">
          <div>
            <Title order={3}>Project Progress</Title>
            <Text size="sm" c="dimmed" mt={4}>
              Timeline and milestones
            </Text>
          </div>
          <Badge color={getStatusColor()} size="lg" variant="light">
            {getStatusText()}
          </Badge>
        </Group>

        {/* Progress Bar */}
        <Box>
          <Group justify="space-between" mb={8}>
            <Text size="sm" fw={500}>
              Overall Progress
            </Text>
            <Text size="sm" fw={600} c={getStatusColor()}>
              {progressPercentage}%
            </Text>
          </Group>
          <Progress value={progressPercentage} color={getStatusColor()} size="lg" radius="md" />
        </Box>

        {/* Timeline */}
        <Timeline
          active={displayMilestones.findIndex((m) => m.status === "in_progress")}
          bulletSize={24}
          lineWidth={2}
        >
          {displayMilestones.map((milestone, index) => (
            <Timeline.Item
              key={milestone.id || index}
              bullet={getStatusIcon(milestone.status)}
              title={
                <Group gap="xs">
                  <span>{milestone.name}</span>
                  {milestone.canComplete && milestone.id && (
                    <Tooltip label="Mark as complete">
                      <ActionIcon
                        size="xs"
                        variant="subtle"
                        color="green"
                        onClick={() => handleMarkComplete(milestone)}
                        loading={updateMilestone.isPending}
                      >
                        <IconCircleCheck size={14} />
                      </ActionIcon>
                    </Tooltip>
                  )}
                </Group>
              }
              color={getStatusColor2(milestone.status)}
            >
              <Text size="sm" c="dimmed" mt={4}>
                {formatDate(milestone.date)}
              </Text>
              {milestone.status === "in_progress" && (
                <Badge color="blue" size="sm" mt={8} variant="light">
                  In Progress
                </Badge>
              )}
              {milestone.status === "completed" && (
                <Badge color="green" size="sm" mt={8} variant="light">
                  Completed
                </Badge>
              )}
              {milestone.status === "skipped" && (
                <Badge color="gray" size="sm" mt={8} variant="light">
                  Skipped
                </Badge>
              )}
            </Timeline.Item>
          ))}
        </Timeline>

        {/* Today Marker */}
        <Paper
          p="md"
          withBorder
          style={{ borderStyle: "dashed", backgroundColor: "var(--mantine-color-blue-0)" }}
        >
          <Group gap="xs">
            <IconClock size={16} style={{ color: "var(--mantine-color-blue-6)" }} />
            <Text size="sm" fw={500} c="blue">
              Today: {formatDate(new Date().toISOString())}
            </Text>
          </Group>
        </Paper>
      </Stack>
    </Paper>
  );
}
